import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { useTranslation } from 'react-i18next';

export type PartialAcceptLine = {
  id: string;
  label: string;
};

type Props = {
  open: boolean;
  charged: PartialAcceptLine[];
  declined: PartialAcceptLine[];
  chargedTotalLabel: string;
  reason: string;
  loading: boolean;
  onReasonChange: (value: string) => void;
  onClose: () => void;
  onConfirm: () => void;
};

export default function HostRecurringPartialAcceptDialog({
  open,
  charged,
  declined,
  chargedTotalLabel,
  reason,
  loading,
  onReasonChange,
  onClose,
  onConfirm,
}: Props) {
  const { t } = useTranslation();
  const canConfirm = reason.trim().length > 0 && charged.length > 0 && !loading;

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {t('roomBookingRequests.partialAcceptTitle', 'Accept selected sessions')}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t(
            'roomBookingRequests.partialAcceptMessage',
            'The guest is charged only for the selected sessions. Every other pending session in this series is declined.',
          )}
        </Typography>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">
            {t('roomBookingRequests.partialAcceptCharged', 'Charged')}
          </Typography>
          <Typography variant="subtitle1" fontWeight={800}>
            {chargedTotalLabel}
          </Typography>
        </Stack>
        <Stack spacing={0.75} sx={{ mb: 2 }}>
          {charged.map((line) => (
            <Stack key={line.id} direction="row" spacing={1} alignItems="center">
              <CheckCircleOutlineIcon color="primary" fontSize="small" />
              <Typography variant="body2">{line.label}</Typography>
            </Stack>
          ))}
        </Stack>
        {declined.length > 0 ? (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('roomBookingRequests.partialAcceptDeclined', 'Declined')}
            </Typography>
            <Stack spacing={0.75} sx={{ mb: 2 }}>
              {declined.map((line) => (
                <Stack key={line.id} direction="row" spacing={1} alignItems="center">
                  <CancelOutlinedIcon color="error" fontSize="small" />
                  <Typography variant="body2">{line.label}</Typography>
                </Stack>
              ))}
            </Stack>
          </>
        ) : null}
        <TextField
          fullWidth
          required
          multiline
          minRows={2}
          label={t('roomBookingRequests.declineReason', 'Reason')}
          value={reason}
          onChange={(event) => onReasonChange(event.target.value)}
          disabled={loading}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button variant="contained" onClick={onConfirm} disabled={!canConfirm}>
          {t('roomBookingRequests.acceptSelected', 'Accept selected ({{count}})', {
            count: charged.length,
          })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
